import { z } from "zod";
import { Timestamp } from "./common.ts";
import { gridBilinear } from "./weather-grid.ts";

/**
 * Wind overlay for the overview map: a MODEL FORECAST, never a measurement.
 *
 * Source: the MeteoSwiss ICON-CH model (CC BY 4.0, credit "MeteoSwiss"), fetched by the API from
 * Open-Meteo as a regular WGS84 grid and cached (never fetched by the client). The UI says
 * "Modellprognose" and credits MeteoSwiss and Open-Meteo while the overlay is on.
 *
 * Grid: WIND_GRID — `nx` × `ny` points, point (i, j) at lon = west + i·dLon, lat = south + j·dLat.
 * Values are row-major from the south-west: index = j·nx + i (j grows NORTH).
 *
 * Components: `u` (towards east) and `v` (towards north) in WIND_UNIT_MS = 0.1 m/s integers, so
 * a value of 52 is 5.2 m/s. They describe where the air MOVES TO; the meteorological direction
 * (where the wind comes FROM) converts with `windUV`. A missing value is `null`, never 0
 * (0 means calm).
 *
 * Height: wind AT LAKE LEVEL. A grid point's own model terrain can be a ridge (up to ~2 600 m next
 * to a lake), and bilinear interpolation would blend summit wind into the lake. So the API asks
 * Open-Meteo for every point within WIND_LAKE_NEAR_KM of a lake (its bbox) at `elevation` = that
 * lake's `waterLevelM` (the nearest lake when several are near); every other point sends `nan`
 * (no downscaling: the model grid-cell height). Open-Meteo needs exactly one `elevation` value per
 * coordinate, so every chunk sends a full list. Same call count.
 *
 * Time: `times[k]` (hourly, UTC, ascending) is the validity time of `u[k]` / `v[k]`. The API trims
 * `times` to start WIND_PAST_HOURS before the current hour (owner, 2026-11-04: wind and rain must
 * animate together, so the measured radar steps of the last RADAR_WINDOW_MS need the model wind
 * of THEIR time) and covers at least WIND_MIN_HOURS hours AHEAD of the current hour when it can.
 * Upstream it asks `past_hours=WIND_UPSTREAM_PAST_HOURS` in the same call (no extra calls; the
 * budget below is unchanged). The past hours are still the model (never a measurement); the UI
 * keeps saying "Modell". Client: the wind-only slider (and the forecast part of the radar bar)
 * still starts at the current hour (`max(times[0], current hour)`); the past hours are used only
 * at measured radar steps. A cached field from before this change (starting at the current hour)
 * stays valid: radar steps before its first time show "keine Winddaten" until the next refresh.
 * Whether a time HAS wind is decided only by the field's own span (`windCovers`: times[0] ≤ t ≤
 * last), NEVER by the slider range; the client's "keine Winddaten" check and the wind layer both
 * use it, and `createWindSampler` returns null outside that span (no clamping to another hour).
 * Upstream times come WITHOUT a zone ("2026-10-28T16:00") but are UTC (`timezone=UTC`): append
 * ":00Z" before parsing (a bare `Date.parse` reads them as LOCAL time).
 * `runAt` is the model run (Open-Meteo meta.json `last_run_initialisation_time`), null if unknown.
 *
 * Budget (Open-Meteo free tier, < 10 000 calls a day, limited per IP; every grid point counts as a
 * call): the API refreshes lazily and at most once per WIND_REFRESH_MS counted from the last
 * ATTEMPT of any outcome (a failed or discarded refresh spends its calls too), i.e. ≤ 8 a day.
 * Only while there is no field at all, failed attempts back off 5, 10, 20 ... min, capped at
 * WIND_REFRESH_MS. It keeps a per-UTC-day call counter persisted next to the cached field (a
 * restart must not reset it, so the attempt and its calls are persisted BEFORE requests go out),
 * checks WIND_DAILY_CALL_LIMIT before EVERY upstream request (retries count) and pauses refreshes
 * for the rest of the UTC day once the next request would cross it. Only a field that passes
 * WindField is ever cached, served or persisted.
 * It requests WIND_FETCH_HOURS so the slider keeps ≥ 24 h between refreshes. Never shrink the
 * grid spacing without redoing the budget.
 *
 * Credit (Open-Meteo licence, CC BY 4.0): while the overlay is on, the UI visibly shows that it is
 * a model ("Modell"), names MeteoSwiss as the model's source, LINKS "Open-Meteo" to
 * WIND_ATTRIBUTION_URL and the licence to WIND_LICENCE_URL, and says the data were changed
 * ("interpoliert"). The short form "Modell · MeteoSwiss via Open-Meteo · CC BY 4.0 · interpoliert"
 * suffices (full WIND_ATTRIBUTION, ICON-CH and the model run may sit in the info panel). On phones
 * the credit lives in the map footer, on wider screens in the time bar; never cut or covered.
 */
export const WIND_GRID = {
  west: 5.85,
  south: 45.65,
  dLon: 0.15,
  dLat: 0.1,
  nx: 33,
  ny: 24,
} as const;
/** One integer unit of `u` / `v` in m/s. */
export const WIND_UNIT_MS = 0.1;
/** Forecast hours the API aims to serve from the current hour (owner: "now + ~24 h"). */
export const WIND_HOURS = 25;
/** Hours requested upstream per refresh (covers WIND_HOURS for a full WIND_REFRESH_MS). */
export const WIND_FETCH_HOURS = 30;
/**
 * Hours served BEFORE the current hour. Must satisfy hour(now) − WIND_PAST_HOURS·1 h ≤ now −
 * RADAR_WINDOW_MS − RADAR_STALE_MS (the oldest radar step at the worst radar latency), so 3.
 */
export const WIND_PAST_HOURS = 3;
/** `past_hours` asked upstream: WIND_PAST_HOURS plus one hour of margin. */
export const WIND_UPSTREAM_PAST_HOURS = 4;
/** Below this many hours ahead the API refreshes (stale field). */
export const WIND_MIN_HOURS = 20;
/** ICON-CH1 runs every 3 h; never refresh more often. */
export const WIND_REFRESH_MS = 3 * 60 * 60 * 1000;
/** Upstream calls (grid points) per UTC day before refreshes pause; free tier is < 10 000. */
export const WIND_DAILY_CALL_LIMIT = 8000;
export const WIND_ATTRIBUTION = "MeteoSwiss ICON-CH via Open-Meteo";
export const WIND_ATTRIBUTION_URL = "https://open-meteo.com/";
export const WIND_LICENCE_URL = "https://creativecommons.org/licenses/by/4.0/";
/** Points this close to a lake bbox are requested at the lake's water level (see "Height"). */
export const WIND_LAKE_NEAR_KM = 15;
/**
 * Speed ramp thresholds (km/h) for the `wind-0..3` tokens: < 10, 10–20, 20–35, ≥ 35. Shared by
 * the legend and the reduced-motion arrows. Speed is never colour-only (WCAG 1.4.1): arrow length
 * (and weight) grows with the band, and the legend shows the same lengths.
 */
export const WIND_RAMP_KMH = [10, 20, 35] as const;
/** Sanity bound of one component in WIND_UNIT_MS units (±100 m/s). */
export const WIND_MAX_UNITS = 1000;

const Component = z.array(z.number().int().min(-WIND_MAX_UNITS).max(WIND_MAX_UNITS).nullable());

export const WindGrid = z
  .object({
    west: z.number(),
    south: z.number(),
    dLon: z.number().positive(),
    dLat: z.number().positive(),
    nx: z.number().int().min(2),
    ny: z.number().int().min(2),
  })
  .strict();
export type WindGrid = z.infer<typeof WindGrid>;

/** GET /api/weather/wind (public). */
export const WindField = z
  .object({
    model: z.literal("meteoswiss-icon-ch"),
    /** When the API fetched this field from upstream. */
    fetchedAt: Timestamp,
    /** Model run (initialisation time), null when upstream did not say. */
    runAt: Timestamp.nullable(),
    times: z.array(Timestamp).min(1),
    grid: WindGrid,
    u: z.array(Component),
    v: z.array(Component),
    attribution: z.string(),
  })
  .strict()
  .superRefine((f, ctx) => {
    const n = f.grid.nx * f.grid.ny;
    for (const key of ["u", "v"] as const) {
      if (f[key].length !== f.times.length)
        ctx.addIssue({ code: "custom", path: [key], message: "one array per time" });
      f[key].forEach((a, k) => {
        if (a.length !== n)
          ctx.addIssue({ code: "custom", path: [key, k], message: `expected ${n} values` });
      });
    }
    for (let k = 1; k < f.times.length; k++) {
      if (Date.parse(f.times[k]!) <= Date.parse(f.times[k - 1]!))
        ctx.addIssue({ code: "custom", path: ["times", k], message: "times must ascend" });
    }
  });
export type WindField = z.infer<typeof WindField>;

/**
 * Meteorological speed (m/s) and direction (degrees, where the wind comes FROM, 0 = north,
 * 90 = east) → u (east) / v (north) in m/s. A north wind (0°) blows south: v < 0.
 */
export function windUV(speedMs: number, dirFromDeg: number): { u: number; v: number } {
  const th = (dirFromDeg * Math.PI) / 180;
  return { u: -speedMs * Math.sin(th), v: -speedMs * Math.cos(th) };
}

/** u/v (m/s) → meteorological speed and FROM-direction in [0, 360). Calm → direction 0. */
export function windSpeedDir(u: number, v: number): { speedMs: number; dirFromDeg: number } {
  const speedMs = Math.hypot(u, v);
  if (speedMs === 0) return { speedMs: 0, dirFromDeg: 0 };
  const deg = (Math.atan2(-u, -v) * 180) / Math.PI;
  return { speedMs, dirFromDeg: (deg + 360) % 360 };
}

/** Bilinear sample of one hour at (lon, lat); null outside the grid or on any null corner. */
function sampleHour(
  grid: WindGrid,
  u: readonly (number | null)[],
  v: readonly (number | null)[],
  lon: number,
  lat: number,
): { u: number; v: number } | null {
  const su = gridBilinear(grid, u, lon, lat);
  if (su === null) return null;
  const sv = gridBilinear(grid, v, lon, lat);
  if (sv === null) return null;
  return { u: su * WIND_UNIT_MS, v: sv * WIND_UNIT_MS };
}

/** True iff `tMs` lies within the field's own time span (first ≤ t ≤ last hour). */
export function windCovers(times: readonly string[], tMs: number): boolean {
  if (times.length === 0 || !Number.isFinite(tMs)) return false;
  return Date.parse(times[0]!) <= tMs && tMs <= Date.parse(times[times.length - 1]!);
}

/**
 * Wind at (lon, lat) and time `tMs` (epoch ms) in m/s: bilinear in space, linear in time between
 * the two surrounding hours. Null outside the field's time span (`windCovers`; never a value from
 * another hour), outside the grid or wherever a needed corner is missing (draw nothing there;
 * never treat as calm).
 *
 * `createWindSampler` parses `times` once; use it in per-frame loops (particles).
 */
export function createWindSampler(
  field: Pick<WindField, "grid" | "times" | "u" | "v">,
): (lon: number, lat: number, tMs: number) => { u: number; v: number } | null {
  const ts = field.times.map((t) => Date.parse(t));
  const last = ts.length - 1;
  return (lon, lat, tMs) => {
    if (Number.isNaN(tMs) || tMs < ts[0]! || tMs > ts[last]!) return null;
    let k = 0;
    while (k < last && ts[k + 1]! <= tMs) k++;
    const a = sampleHour(field.grid, field.u[k]!, field.v[k]!, lon, lat);
    if (k === last || tMs <= ts[k]!) return a;
    const b = sampleHour(field.grid, field.u[k + 1]!, field.v[k + 1]!, lon, lat);
    if (a === null || b === null) return null;
    const f = (tMs - ts[k]!) / (ts[k + 1]! - ts[k]!);
    return { u: a.u + (b.u - a.u) * f, v: a.v + (b.v - a.v) * f };
  };
}

export function sampleWind(
  field: Pick<WindField, "grid" | "times" | "u" | "v">,
  lon: number,
  lat: number,
  tMs: number,
): { u: number; v: number } | null {
  return createWindSampler(field)(lon, lat, tMs);
}

/** All grid points (lon, lat) in value order (index = j·nx + i), for the upstream request. */
export function windGridPoints(grid: WindGrid = WIND_GRID): { lon: number; lat: number }[] {
  const pts: { lon: number; lat: number }[] = [];
  for (let j = 0; j < grid.ny; j++)
    for (let i = 0; i < grid.nx; i++)
      pts.push({
        lon: Math.round((grid.west + i * grid.dLon) * 1e4) / 1e4,
        lat: Math.round((grid.south + j * grid.dLat) * 1e4) / 1e4,
      });
  return pts;
}
